import { useEffect } from "react";
import { useStoreActions, useStoreState } from "../../../../store";
import { TransferDirection } from "../../../../types";
import { useIsMobile } from "../../../../helpers/ui";
import Page from "../../../../components/Layout/Page";
import T from "../../../../components/Typography";
import { TransfersPageContent } from "./components";
import { MobileHeader, DesktopHeader } from "../Account/components";
import SelectAccount from "./01SelectAccount";
import SelectAmount from "./02SelectAmount";
import ReviewTransfer from "./03ReviewTransfer";
import Verification from "./04Verification";
import { colors } from "../../../../styles";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import { employeeTransfersPath } from "../../../../routes/paths";

const TransferFlow = ({ direction }: { direction: TransferDirection }) => {
  const transferState = useStoreState((state) => state.transfer);
  const transferActions = useStoreActions((actions) => actions.transfer);
  const isMobile = useIsMobile();

  useEffect(() => {
    transferActions.setDirection(direction);

    if (direction === "add_money") {
      transferActions.setCurrentStep("SELECT_ACCOUNT");
    }
  }, [direction, transferActions]);

  let Component;
  switch (transferState.currentStep) {
    case "SEND_WARNING":
    case "SELECT_ACCOUNT":
      Component = SelectAccount;
      break;
    case "SELECT_AMOUNT":
      Component = SelectAmount;
      break;
    case "REVIEW_TRANSFER":
      Component = ReviewTransfer;
      break;
    case "TWO_FACTOR_VERIFICATION":
      Component = Verification;
      break;
    default:
      Component = SelectAccount;
      break;
  }

  const getTitleSubtitle = () => {
    let title = "";
    let subtitle = "";
    const isAddMoney = transferState.direction === "add_money";
    switch (transferState.currentStep) {
      case "SEND_WARNING":
      case "SELECT_ACCOUNT":
        title = isAddMoney ? "Add Money" : "Send Money";
        subtitle = isAddMoney
          ? "Which account should we receive money from?"
          : "Send money from this account to a connected account";
        break;
      case "SELECT_AMOUNT":
        title = "Select Amount";
        subtitle = "Enter the amount you'd like to receive from this account";
        break;
      case "REVIEW_TRANSFER":
        title = isAddMoney ? "Review Deposit" : "Review Transfer";
        subtitle = "Make sure the information looks correct";
        break;
      case "TWO_FACTOR_VERIFICATION":
        title = "Verification";
        subtitle = "Confirm your identity";
        break;
    }
    return { title, subtitle };
  };

  const getBreakcrumbs = () => {
    const breadcrumbs = ["Transfers"];
    const addSelectAccount = () => {
      if (direction === "add_money") {
        breadcrumbs.push("Add Money");
      } else {
        breadcrumbs.push("Send Money");
      }
    };
    const addSelectAmount = () => {
      breadcrumbs.push("Select Amount");
    };
    const addReviewTransfer = () => {
      if (direction === "add_money") {
        breadcrumbs.push("Review Deposit");
      } else {
        breadcrumbs.push("Review Transfer");
      }
    };
    switch (transferState.currentStep) {
      case "SEND_WARNING":
      case "SELECT_ACCOUNT":
        addSelectAccount();
        break;
      case "SELECT_AMOUNT":
        addSelectAccount();
        addSelectAmount();
        break;
      case "REVIEW_TRANSFER":
        addSelectAccount();
        addSelectAmount();
        addReviewTransfer();
        break;
      case "TWO_FACTOR_VERIFICATION":
        addSelectAccount();
        addSelectAmount();
        addReviewTransfer();
        breadcrumbs.push("Verification");
    }
    return breadcrumbs;
  };

  const doBack = () => {
    if (transferState.previousStep) {
      transferActions.setCurrentStep(transferState.previousStep);
    }
  };

  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={getTitleSubtitle().title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeTransfersPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs
            breadcrumbs={getBreakcrumbs()}
            handleBack={doBack}
          />
        </DesktopHeader>
      )}
      <TransfersPageContent>
        <div style={{ marginTop: !isMobile ? "32px" : "0px" }}>
          {!isMobile && (
            <div>
              <T variant="headingMd">{getTitleSubtitle().title}</T>
              <T variant="lgMed" color={colors.gray_600}>
                {getTitleSubtitle().subtitle}
              </T>
            </div>
          )}
        </div>
        <Component />
      </TransfersPageContent>
    </Page>
  );
};

export default TransferFlow;
